// ACA Plan Intelligence (Release 1) — shop / compare / save ACA
// individual-market plans against the live CMS Marketplace API.
//
// Flow: 4-step intake wizard (Location -> Household -> Care Needs ->
// Preferences) -> POST /api/aca/plan-search -> results (cards +
// sortable table + compare up to 4 + filters).
//
// Anti-fabrication: every plan fact shown here comes straight off the
// server response (api/aca/plan-search.js), which itself only ever
// echoes CMS data or a real computed value. A missing field renders
// as "-", never a guess. Provider/drug matches are NOT verified this
// release — that badge always reads "Not checked yet (Release 2)".
//
// Out of scope this release: deterministic Best-Overall scoring,
// Nova explanations, PDF / secure client link, enrollment submission.

import React from 'react';
import {
  MapPin, Users, Stethoscope, SlidersHorizontal, Search, X, ChevronRight,
  ChevronLeft, Check, Plus, Trash2, AlertTriangle,
  Loader2, Clock, LayoutGrid, Table as TableIcon,
} from 'lucide-react';
import { readPersistedJwtDirect } from '../lib/authedFetch';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const METAL_LEVELS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Catastrophic'];
const PLAN_TYPES = ['HMO', 'PPO', 'EPO', 'POS'];
const RELATIONSHIPS = [
  { id: 'self', label: 'Self' },
  { id: 'spouse', label: 'Spouse' },
  { id: 'dependent', label: 'Dependent' },
  { id: 'other', label: 'Other' },
];
const UTILIZATION = [
  { id: 'low', label: 'Low — rarely visits a doctor' },
  { id: 'medium', label: 'Medium — occasional visits / a maintenance med' },
  { id: 'high', label: 'High — chronic condition / frequent care' },
];
const PRIORITY_OPTIONS = [
  { id: 'low_premium', label: 'Lowest premium' },
  { id: 'low_deductible', label: 'Lowest deductible' },
  { id: 'keep_doctors', label: 'Keep current doctors' },
  { id: 'keep_prescriptions', label: 'Keep current prescriptions' },
  { id: 'hsa_eligible', label: 'HSA-eligible' },
  { id: 'brand_carrier', label: 'Prefer a specific carrier' },
];

function money(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function moneyPrecise(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function emptyMember() {
  return { relationship: 'self', dob: '', gender: '', applying_coverage: true, tobacco_use: false, pregnant: false, has_existing_coverage: false, existing_coverage_type: '', expected_utilization: 'low' };
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AcaPlanIntelligenceView({ currentUser, showToast = () => {}, openClientProfile = () => {} }) {
  const [phase, setPhase] = React.useState('wizard'); // 'wizard' | 'results'
  const [step, setStep] = React.useState(1);

  const [location, setLocation] = React.useState({ state: '', zip: '', county_fips: '', effective_date: todayIso(), plan_year: new Date().getFullYear() });
  const [counties, setCounties] = React.useState([]);
  const [countyLookupState, setCountyLookupState] = React.useState({ loading: false, degraded: false, reason: null });

  const [household, setHousehold] = React.useState({ annual_income: '', members: [emptyMember()] });
  const [doctors, setDoctors] = React.useState([]);
  const [prescriptions, setPrescriptions] = React.useState([]);
  const [preferences, setPreferences] = React.useState({ max_monthly_premium: '', metal_levels: [], plan_types: [], hsa_eligible_only: false, priorities: [] });

  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null); // full /api/aca/plan-search response

  const [history, setHistory] = React.useState({ searches: [], drafts: [], loading: true });

  const loadHistory = React.useCallback(async () => {
    setHistory((h) => ({ ...h, loading: true }));
    try {
      const j = await apiFetch('/api/aca/searches');
      setHistory({ searches: j.searches || [], drafts: j.drafts || [], loading: false, degraded: !!j.degraded });
    } catch (err) {
      setHistory({ searches: [], drafts: [], loading: false, error: String(err.message || err) });
    }
  }, []);
  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── ZIP -> county auto-resolve ─────────────────────────────────────
  const zipLookupRef = React.useRef(null);
  React.useEffect(() => {
    const zip = String(location.zip || '').replace(/\D/g, '');
    setCounties([]);
    setCountyLookupState({ loading: false, degraded: false, reason: null });
    if (zip.length !== 5) return;
    if (zipLookupRef.current) clearTimeout(zipLookupRef.current);
    zipLookupRef.current = setTimeout(async () => {
      setCountyLookupState({ loading: true, degraded: false, reason: null });
      try {
        const j = await apiFetch(`/api/aca/county-lookup?zip=${zip}`);
        if (j.degraded) {
          setCountyLookupState({ loading: false, degraded: true, reason: j.reason });
          setCounties([]);
        } else {
          setCounties(j.counties || []);
          setCountyLookupState({ loading: false, degraded: false, reason: null });
          if ((j.counties || []).length === 1) {
            setLocation((l) => ({ ...l, county_fips: j.counties[0].fips }));
          } else {
            setLocation((l) => ({ ...l, county_fips: '' }));
          }
        }
      } catch (err) {
        setCountyLookupState({ loading: false, degraded: true, reason: 'lookup_failed' });
      }
    }, 400);
    return () => { if (zipLookupRef.current) clearTimeout(zipLookupRef.current); };
  }, [location.zip]);

  // ── validation gates per step ──────────────────────────────────────
  const zipDigits = String(location.zip || '').replace(/\D/g, '');
  const needsCountyPick = counties.length > 1 && !location.county_fips;
  const step1Valid = /^[A-Za-z]{2}$/.test(location.state) && zipDigits.length === 5 && !needsCountyPick && !!location.effective_date && Number.isInteger(Number(location.plan_year));
  const step2Valid = Number(household.annual_income) >= 0 && household.members.length > 0 &&
    household.members.every((m) => m.relationship && m.dob) &&
    household.members.some((m) => m.applying_coverage !== false);
  const step3Valid = true; // capture-only, never required
  const step4Valid = true;

  const stepValid = { 1: step1Valid, 2: step2Valid, 3: step3Valid, 4: step4Valid };

  const buildPayload = () => ({
    location: { ...location, zip: zipDigits },
    household: { annual_income: Number(household.annual_income) || 0, members: household.members },
    preferences,
    doctors: doctors.filter((d) => d.name?.trim()),
    prescriptions: prescriptions.filter((p) => p.drug_name?.trim()),
  });

  const saveDraft = async () => {
    try {
      await apiFetch('/api/aca/searches', { method: 'POST', body: JSON.stringify(buildPayload()) });
      showToast('Household saved — find it under Saved drafts.');
      loadHistory();
    } catch (err) { showToast(`Save failed: ${err.message}`); }
  };

  const runSearch = async () => {
    if (!step1Valid || !step2Valid) { showToast('Complete Location + Household before searching.'); return; }
    setSubmitting(true);
    try {
      const j = await apiFetch('/api/aca/plan-search', { method: 'POST', body: JSON.stringify(buildPayload()) });
      setResult(j);
      setPhase('results');
      loadHistory();
    } catch (err) {
      showToast(`Search failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const newSearch = () => { setResult(null); setPhase('wizard'); setStep(1); };

  return (
    <div className="space-y-4">
      <div className="border-2 border-emerald-900 bg-emerald-900 rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">ACA Plan Intelligence</div>
          <div className="text-white text-lg font-bold mt-0.5">Shop, compare, and save ACA marketplace plans</div>
          <div className="text-emerald-200 text-xs mt-0.5">Individual market only. Shop / compare / save — no enrollment is submitted here.</div>
        </div>
        {phase === 'results' && (
          <button onClick={newSearch} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
            <Search size={12}/> New search
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <div>
          {phase === 'wizard' && (
            <IntakeWizard
              step={step} setStep={setStep} stepValid={stepValid}
              location={location} setLocation={setLocation}
              counties={counties} countyLookupState={countyLookupState}
              household={household} setHousehold={setHousehold}
              doctors={doctors} setDoctors={setDoctors}
              prescriptions={prescriptions} setPrescriptions={setPrescriptions}
              preferences={preferences} setPreferences={setPreferences}
              submitting={submitting}
              onSaveDraft={saveDraft}
              onSubmit={runSearch}
            />
          )}
          {phase === 'results' && result && (
            <ResultsPanel result={result} showToast={showToast} openClientProfile={openClientProfile}/>
          )}
        </div>
        <HistorySidebar history={history} onReload={loadHistory} showToast={showToast}/>
      </div>
    </div>
  );
}

// ── Intake Wizard ────────────────────────────────────────────────────

function StepDots({ step, stepValid }) {
  const steps = [
    { n: 1, label: 'Location', icon: MapPin },
    { n: 2, label: 'Household', icon: Users },
    { n: 3, label: 'Care Needs', icon: Stethoscope },
    { n: 4, label: 'Preferences', icon: SlidersHorizontal },
  ];
  return (
    <div className="flex items-center gap-1 flex-wrap px-4 pt-3">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const active = step === s.n;
        const done = step > s.n && stepValid[s.n];
        return (
          <React.Fragment key={s.n}>
            {i > 0 && <div className="w-4 h-px bg-slate-300"/>}
            <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${active ? 'bg-emerald-600 text-white border-emerald-700' : done ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
              {done ? <Check size={10}/> : <Icon size={10}/>} {s.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function IntakeWizard(props) {
  const { step, setStep, stepValid, submitting, onSaveDraft, onSubmit } = props;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <StepDots step={step} stepValid={stepValid}/>
      <div className="px-4 py-4">
        {step === 1 && <StepLocation {...props}/>}
        {step === 2 && <StepHousehold {...props}/>}
        {step === 3 && <StepCareNeeds {...props}/>}
        {step === 4 && <StepPreferences {...props}/>}
      </div>
      <div className="bg-slate-50 border-t border-slate-100 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} className="text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-3 py-1.5">
              <ChevronLeft size={12}/> Back
            </button>
          )}
          <button onClick={onSaveDraft} className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 px-3 py-1.5 border border-slate-200 rounded-md">
            Save draft
          </button>
        </div>
        {step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!stepValid[step]}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-md inline-flex items-center gap-1"
          >
            Next <ChevronRight size={12}/>
          </button>
        ) : (
          <button
            onClick={onSubmit}
            disabled={submitting || !stepValid[1] || !stepValid[2]}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-md inline-flex items-center gap-1.5"
          >
            {submitting ? <><Loader2 size={12} className="animate-spin"/> Searching…</> : <><Search size={12}/> Search plans</>}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="text-xs text-slate-600 block">
      {label}
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </label>
  );
}
const inputCls = 'w-full text-sm border border-slate-300 rounded-md px-2 py-1.5';

function StepLocation({ location, setLocation, counties, countyLookupState }) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><MapPin size={13}/> Step 1 — Location</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="State (2-letter)">
          <input value={location.state} maxLength={2} onChange={(e) => setLocation({ ...location, state: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })} placeholder="TX" className={inputCls}/>
        </Field>
        <Field label="ZIP code">
          <input value={location.zip} onChange={(e) => setLocation({ ...location, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} placeholder="78704" className={inputCls}/>
        </Field>
        <Field label="Effective date">
          <input type="date" value={location.effective_date} onChange={(e) => setLocation({ ...location, effective_date: e.target.value })} className={inputCls}/>
        </Field>
        <Field label="Market year">
          <input type="number" value={location.plan_year} onChange={(e) => setLocation({ ...location, plan_year: e.target.value })} className={inputCls}/>
        </Field>
      </div>
      {countyLookupState.loading && <div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Resolving county…</div>}
      {countyLookupState.degraded && (
        <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-xs text-amber-900">
          County auto-resolve is unavailable right now ({countyLookupState.reason === 'no_cms_key' ? 'CMS Marketplace API key not set' : countyLookupState.reason}). You can still continue — county is optional if the plan search can resolve it from ZIP alone.
        </div>
      )}
      {counties.length > 1 && (
        <Field label={`This ZIP spans ${counties.length} counties — pick one`}>
          <select value={location.county_fips} onChange={(e) => setLocation({ ...location, county_fips: e.target.value })} className={inputCls + ' bg-white'}>
            <option value="">Select a county…</option>
            {counties.map((c) => <option key={c.fips} value={c.fips}>{c.name || c.fips} ({c.fips})</option>)}
          </select>
        </Field>
      )}
      {counties.length === 1 && (
        <div className="text-xs text-emerald-700 inline-flex items-center gap-1"><Check size={12}/> County resolved: {counties[0].name || counties[0].fips}</div>
      )}
    </div>
  );
}

function StepHousehold({ household, setHousehold }) {
  const updateMember = (i, patch) => {
    const members = household.members.slice();
    members[i] = { ...members[i], ...patch };
    setHousehold({ ...household, members });
  };
  const addMember = () => setHousehold({ ...household, members: [...household.members, emptyMember()] });
  const removeMember = (i) => setHousehold({ ...household, members: household.members.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Users size={13}/> Step 2 — Household</div>
      <Field label="Annual household income ($)">
        <input type="number" min="0" value={household.annual_income} onChange={(e) => setHousehold({ ...household, annual_income: e.target.value })} placeholder="50000" className={inputCls + ' max-w-xs'}/>
      </Field>

      <div className="space-y-2">
        {household.members.map((m, i) => (
          <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Member {i + 1}</div>
              {household.members.length > 1 && (
                <button onClick={() => removeMember(i)} className="text-rose-600 hover:text-rose-800"><Trash2 size={13}/></button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field label="Relationship">
                <select value={m.relationship} onChange={(e) => updateMember(i, { relationship: e.target.value })} className={inputCls + ' bg-white'}>
                  {RELATIONSHIPS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Date of birth">
                <input type="date" value={m.dob} onChange={(e) => updateMember(i, { dob: e.target.value })} className={inputCls}/>
              </Field>
              <Field label="Expected utilization">
                <select value={m.expected_utilization} onChange={(e) => updateMember(i, { expected_utilization: e.target.value })} className={inputCls + ' bg-white'}>
                  {UTILIZATION.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={m.applying_coverage !== false} onChange={(e) => updateMember(i, { applying_coverage: e.target.checked })}/> Applying for coverage</label>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!m.tobacco_use} onChange={(e) => updateMember(i, { tobacco_use: e.target.checked })}/> Tobacco use</label>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!m.pregnant} onChange={(e) => updateMember(i, { pregnant: e.target.checked })}/> Pregnant</label>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!m.has_existing_coverage} onChange={(e) => updateMember(i, { has_existing_coverage: e.target.checked })}/> Has existing coverage</label>
            </div>
            {m.has_existing_coverage && (
              <input value={m.existing_coverage_type} onChange={(e) => updateMember(i, { existing_coverage_type: e.target.value })} placeholder="Existing coverage type (e.g. employer plan, Medicaid)" className={inputCls}/>
            )}
          </div>
        ))}
      </div>
      <button onClick={addMember} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={12}/> Add household member</button>
    </div>
  );
}

function StepCareNeeds({ doctors, setDoctors, prescriptions, setPrescriptions }) {
  const addDoctor = () => setDoctors([...doctors, { name: '', npi: '', specialty: '', notes: '' }]);
  const updateDoctor = (i, patch) => { const d = doctors.slice(); d[i] = { ...d[i], ...patch }; setDoctors(d); };
  const removeDoctor = (i) => setDoctors(doctors.filter((_, idx) => idx !== i));

  const addRx = () => setPrescriptions([...prescriptions, { drug_name: '', ndc: '', dosage: '', notes: '' }]);
  const updateRx = (i, patch) => { const p = prescriptions.slice(); p[i] = { ...p[i], ...patch }; setPrescriptions(p); };
  const removeRx = (i) => setPrescriptions(prescriptions.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Stethoscope size={13}/> Step 3 — Care Needs</div>
      <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-xs text-sky-900">
        Captured now for reference. Network / formulary verification against a specific plan is <strong>Release 2</strong> — nothing here claims a doctor or drug was checked against any plan yet.
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Doctors</div>
        <div className="space-y-2">
          {doctors.map((d, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <input value={d.name} onChange={(e) => updateDoctor(i, { name: e.target.value })} placeholder="Doctor name" className={inputCls + ' flex-1 min-w-[140px]'}/>
              <input value={d.specialty} onChange={(e) => updateDoctor(i, { specialty: e.target.value })} placeholder="Specialty (optional)" className={inputCls + ' flex-1 min-w-[140px]'}/>
              <input value={d.npi} onChange={(e) => updateDoctor(i, { npi: e.target.value })} placeholder="NPI (optional)" className={inputCls + ' w-32'}/>
              <button onClick={() => removeDoctor(i)} className="text-rose-600 hover:text-rose-800"><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
        <button onClick={addDoctor} className="mt-2 text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={12}/> Add doctor</button>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Prescriptions</div>
        <div className="space-y-2">
          {prescriptions.map((p, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <input value={p.drug_name} onChange={(e) => updateRx(i, { drug_name: e.target.value })} placeholder="Drug name" className={inputCls + ' flex-1 min-w-[140px]'}/>
              <input value={p.dosage} onChange={(e) => updateRx(i, { dosage: e.target.value })} placeholder="Dosage (optional)" className={inputCls + ' flex-1 min-w-[140px]'}/>
              <input value={p.ndc} onChange={(e) => updateRx(i, { ndc: e.target.value })} placeholder="NDC (optional)" className={inputCls + ' w-32'}/>
              <button onClick={() => removeRx(i)} className="text-rose-600 hover:text-rose-800"><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
        <button onClick={addRx} className="mt-2 text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={12}/> Add prescription</button>
      </div>
    </div>
  );
}

function StepPreferences({ preferences, setPreferences }) {
  const toggleIn = (key, val) => {
    const list = preferences[key] || [];
    setPreferences({ ...preferences, [key]: list.includes(val) ? list.filter((v) => v !== val) : [...list, val] });
  };
  return (
    <div className="space-y-3">
      <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><SlidersHorizontal size={13}/> Step 4 — Preferences</div>
      <Field label="Max monthly premium ($, optional)">
        <input type="number" min="0" value={preferences.max_monthly_premium} onChange={(e) => setPreferences({ ...preferences, max_monthly_premium: e.target.value })} placeholder="No limit" className={inputCls + ' max-w-xs'}/>
      </Field>
      <div>
        <div className="text-xs text-slate-600 mb-1">Metal levels</div>
        <div className="flex flex-wrap gap-1.5">
          {METAL_LEVELS.map((m) => (
            <button key={m} onClick={() => toggleIn('metal_levels', m)} className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${preferences.metal_levels.includes(m) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{m}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-600 mb-1">Plan types</div>
        <div className="flex flex-wrap gap-1.5">
          {PLAN_TYPES.map((t) => (
            <button key={t} onClick={() => toggleIn('plan_types', t)} className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${preferences.plan_types.includes(t) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{t}</button>
          ))}
        </div>
      </div>
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
        <input type="checkbox" checked={preferences.hsa_eligible_only} onChange={(e) => setPreferences({ ...preferences, hsa_eligible_only: e.target.checked })}/> HSA-eligible plans only
      </label>
      <div>
        <div className="text-xs text-slate-600 mb-1">Priorities (helps you eyeball the results, doesn't filter)</div>
        <div className="flex flex-wrap gap-1.5">
          {PRIORITY_OPTIONS.map((p) => (
            <button key={p.id} onClick={() => toggleIn('priorities', p.id)} className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${preferences.priorities.includes(p.id) ? 'bg-sky-600 text-white border-sky-700' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{p.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Results ───────────────────────────────────────────────────────────

function ResultsPanel({ result, showToast, openClientProfile }) {
  const [view, setView] = React.useState('cards'); // 'cards' | 'table'
  const [filters, setFilters] = React.useState({ carrier: '', metal: '', type: '', maxNetPremium: '', hsaOnly: false });
  const [sort, setSort] = React.useState({ field: 'premium_net', dir: 'asc' });
  const [compareIds, setCompareIds] = React.useState(new Set());
  const [showCompare, setShowCompare] = React.useState(false);

  if (result.degraded) {
    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 text-center">
        <AlertTriangle className="mx-auto text-amber-600" size={28}/>
        <div className="text-amber-900 font-bold mt-2">
          {result.reason === 'no_cms_key' ? 'Add a CMS Marketplace API key to enable live plan search.' : `Plan search is temporarily unavailable (${result.reason || 'unknown reason'}).`}
        </div>
        <div className="text-amber-800 text-sm mt-1">
          {result.reason === 'no_cms_key'
            ? 'Set CMS_MARKETPLACE_API_KEY in the server environment, then run this search again — your household and preferences were already saved.'
            : 'Your household was saved. Try again shortly, or check the CMS Marketplace API status.'}
        </div>
      </div>
    );
  }

  const plans = result.plans || [];
  const carriers = Array.from(new Set(plans.map((p) => p.carrier_name).filter(Boolean))).sort();
  const metals = Array.from(new Set(plans.map((p) => p.metal_level).filter(Boolean))).sort();
  const types = Array.from(new Set(plans.map((p) => p.plan_type).filter(Boolean))).sort();

  const filtered = plans.filter((p) =>
    (!filters.carrier || p.carrier_name === filters.carrier) &&
    (!filters.metal || p.metal_level === filters.metal) &&
    (!filters.type || p.plan_type === filters.type) &&
    (!filters.hsaOnly || p.hsa_eligible === true) &&
    (!filters.maxNetPremium || (p.premium_net !== null && p.premium_net <= Number(filters.maxNetPremium)))
  );

  const sorted = filtered.slice().sort((a, b) => {
    const av = a[sort.field], bv = b[sort.field];
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return sort.dir === 'asc' ? av - bv : bv - av;
  });

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= 4) { showToast('Compare up to 4 plans at a time.'); return prev; }
        next.add(id);
      }
      return next;
    });
  };

  const comparePlans = plans.filter((p) => compareIds.has(p.id));

  return (
    <div className="space-y-3">
      {result.eligibility?.disclosure && (
        <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-xs text-sky-900">
          {result.eligibility.disclosure}
          {result.eligibility.degraded && (
            <span className="block mt-1 text-amber-800">Subsidy estimate unavailable this run ({result.eligibility.reason}) — net premium is shown only where APTC could be computed.</span>
          )}
        </div>
      )}

      {plans.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
          No plans matched this household in {result.search?.state} {result.search?.zip} for {result.search?.plan_year}. Try widening preferences in Step 4.
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
            <select value={filters.carrier} onChange={(e) => setFilters({ ...filters, carrier: e.target.value })} className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white">
              <option value="">All carriers</option>
              {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.metal} onChange={(e) => setFilters({ ...filters, metal: e.target.value })} className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white">
              <option value="">All metals</option>
              {metals.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white">
              <option value="">All plan types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="number" placeholder="Max net premium" value={filters.maxNetPremium} onChange={(e) => setFilters({ ...filters, maxNetPremium: e.target.value })} className="text-xs border border-slate-300 rounded-md px-2 py-1 w-32"/>
            <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={filters.hsaOnly} onChange={(e) => setFilters({ ...filters, hsaOnly: e.target.checked })}/> HSA only</label>
            <select value={`${sort.field}:${sort.dir}`} onChange={(e) => { const [field, dir] = e.target.value.split(':'); setSort({ field, dir }); }} className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white ml-auto">
              <option value="premium_net:asc">Net premium: low to high</option>
              <option value="premium_net:desc">Net premium: high to low</option>
              <option value="deductible_individual:asc">Deductible: low to high</option>
              <option value="moop_individual:asc">MOOP: low to high</option>
              <option value="quality_rating:desc">Quality rating: high to low</option>
            </select>
            <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
              <button onClick={() => setView('cards')} className={`px-2 py-1 ${view === 'cards' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600'}`}><LayoutGrid size={13}/></button>
              <button onClick={() => setView('table')} className={`px-2 py-1 ${view === 'table' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600'}`}><TableIcon size={13}/></button>
            </div>
          </div>

          {compareIds.size > 0 && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-md px-3 py-2 flex items-center justify-between text-xs">
              <span className="text-emerald-900 font-bold">{compareIds.size} plan{compareIds.size > 1 ? 's' : ''} selected to compare</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCompare(true)} disabled={compareIds.size < 2} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold uppercase tracking-wider px-3 py-1 rounded-md">Compare</button>
                <button onClick={() => setCompareIds(new Set())} className="text-slate-500 hover:text-slate-800">Clear</button>
              </div>
            </div>
          )}

          {view === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {sorted.map((p) => <PlanCard key={p.id} plan={p} selected={compareIds.has(p.id)} onToggleCompare={() => toggleCompare(p.id)}/>)}
            </div>
          ) : (
            <PlanTable plans={sorted} compareIds={compareIds} onToggleCompare={toggleCompare}/>
          )}
        </>
      )}

      {showCompare && (
        <ComparePanel plans={comparePlans} onClose={() => setShowCompare(false)}/>
      )}
    </div>
  );
}

function Badge({ tone, children }) {
  const cls = tone === 'gold' ? 'bg-amber-100 text-amber-800 border-amber-300' :
    tone === 'emerald' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
    tone === 'sky' ? 'bg-sky-100 text-sky-800 border-sky-300' :
    'bg-slate-100 text-slate-600 border-slate-300';
  return <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>{children}</span>;
}

function PlanCard({ plan: p, selected, onToggleCompare }) {
  return (
    <div className={`bg-white border-2 rounded-xl p-3 space-y-2 ${selected ? 'border-emerald-500' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-slate-900">{p.plan_name || 'Unnamed plan'}</div>
          <div className="text-xs text-slate-500">{p.carrier_name || '—'}</div>
        </div>
        <label className="inline-flex items-center">
          <input type="checkbox" checked={selected} onChange={onToggleCompare} title="Add to compare"/>
        </label>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {p.metal_level && <Badge tone="gold">{p.metal_level}</Badge>}
        {p.is_standardized_plan && <Badge tone="sky">Standardized Plan</Badge>}
        {p.hsa_eligible && <Badge tone="emerald">HSA Eligible</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div><span className="text-slate-500">Net premium</span><div className="text-base font-black text-emerald-700 tabular-nums">{money(p.premium_net)}/mo</div></div>
        <div><span className="text-slate-500">Gross premium</span><div className="font-bold tabular-nums">{money(p.premium_gross)}/mo</div></div>
        <div><span className="text-slate-500">Deductible</span><div className="tabular-nums">{money(p.deductible_individual)}</div></div>
        <div><span className="text-slate-500">MOOP</span><div className="tabular-nums">{money(p.moop_individual)}</div></div>
      </div>
      <div className="text-[10px] text-slate-500 flex items-center justify-between border-t border-slate-100 pt-2">
        <span>{p.plan_type || '—'} · HIOS {p.hios_id || '—'}</span>
        <span title="Provider/drug network verification ships in Release 2">Docs/Rx: not checked yet</span>
      </div>
      <div className="text-[9px] text-slate-400">Source: {p.source || 'cms_marketplace'}{p.source_version ? ` · v${p.source_version}` : ''} · as of {p.fetched_at ? new Date(p.fetched_at).toLocaleString() : '—'}</div>
    </div>
  );
}

function PlanTable({ plans, compareIds, onToggleCompare }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
          <tr>
            <th className="px-2 py-2 text-left">Compare</th>
            <th className="px-2 py-2 text-left">Plan</th>
            <th className="px-2 py-2 text-left">Metal</th>
            <th className="px-2 py-2 text-right">Net premium</th>
            <th className="px-2 py-2 text-right">Gross premium</th>
            <th className="px-2 py-2 text-right">Deductible</th>
            <th className="px-2 py-2 text-right">MOOP</th>
            <th className="px-2 py-2 text-center">HSA</th>
            <th className="px-2 py-2 text-right">Quality</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {plans.map((p) => (
            <tr key={p.id}>
              <td className="px-2 py-1.5"><input type="checkbox" checked={compareIds.has(p.id)} onChange={() => onToggleCompare(p.id)}/></td>
              <td className="px-2 py-1.5"><div className="font-bold text-slate-900">{p.plan_name || '—'}</div><div className="text-slate-500">{p.carrier_name || '—'}</div></td>
              <td className="px-2 py-1.5">{p.metal_level || '—'}</td>
              <td className="px-2 py-1.5 text-right font-bold text-emerald-700 tabular-nums">{money(p.premium_net)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{money(p.premium_gross)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{money(p.deductible_individual)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{money(p.moop_individual)}</td>
              <td className="px-2 py-1.5 text-center">{p.hsa_eligible === true ? <Check size={12} className="inline text-emerald-600"/> : p.hsa_eligible === false ? <X size={12} className="inline text-slate-300"/> : '—'}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{p.quality_rating ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComparePanel({ plans, onClose }) {
  const rows = [
    { label: 'Carrier', get: (p) => p.carrier_name },
    { label: 'Plan name', get: (p) => p.plan_name },
    { label: 'Metal level', get: (p) => p.metal_level },
    { label: 'Plan type', get: (p) => p.plan_type },
    { label: 'Net premium / mo', get: (p) => moneyPrecise(p.premium_net) },
    { label: 'Gross premium / mo', get: (p) => moneyPrecise(p.premium_gross) },
    { label: 'Estimated APTC / mo', get: (p) => moneyPrecise(p.aptc_applied) },
    { label: 'Deductible (individual)', get: (p) => money(p.deductible_individual) },
    { label: 'Deductible (family)', get: (p) => money(p.deductible_family) },
    { label: 'MOOP (individual)', get: (p) => money(p.moop_individual) },
    { label: 'MOOP (family)', get: (p) => money(p.moop_family) },
    { label: 'HSA eligible', get: (p) => p.hsa_eligible === true ? 'Yes' : p.hsa_eligible === false ? 'No' : '—' },
    { label: 'Standardized plan', get: (p) => p.is_standardized_plan === true ? 'Yes' : p.is_standardized_plan === false ? 'No' : '—' },
    { label: 'Quality rating', get: (p) => p.quality_rating ?? '—' },
    { label: 'Doctors / prescriptions checked', get: () => 'Not checked yet (Release 2)' },
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="font-bold text-sm">Compare {plans.length} plans</div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-slate-100">
                <td className="px-3 py-2 font-bold text-slate-600 bg-slate-50 w-40">{r.label}</td>
                {plans.map((p) => <td key={p.id} className="px-3 py-2 tabular-nums">{r.get(p)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── History sidebar ──────────────────────────────────────────────────

function HistorySidebar({ history, onReload, showToast }) {
  const del = async (kind, id) => {
    try {
      const qs = kind === 'search' ? `id=${id}` : `household_id=${id}`;
      await apiFetch(`/api/aca/searches?${qs}`, { method: 'DELETE' });
      onReload();
    } catch (err) { showToast(`Delete failed: ${err.message}`); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Clock size={11}/> Recent searches</div>
        {history.loading ? (
          <div className="px-3 py-4 text-xs text-slate-400">Loading…</div>
        ) : (history.searches || []).length === 0 ? (
          <div className="px-3 py-4 text-xs text-slate-400 italic">No searches yet.</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {history.searches.map((s) => (
              <div key={s.id} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold text-slate-900">{s.state} {s.zip}</span>
                  <button onClick={() => del('search', s.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={11}/></button>
                </div>
                <div className="text-slate-500">{s.plan_year} · {s.status === 'ok' ? `${s.results_count} plans` : (s.degraded_reason || s.status)}</div>
                <div className="text-[10px] text-slate-400">{s.created_at ? new Date(s.created_at).toLocaleString() : ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {(history.drafts || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700">Saved drafts</div>
          <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
            {history.drafts.map((h) => (
              <div key={h.id} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold text-slate-900">{h.name || `${h.state || ''} ${h.zip || ''}`}</span>
                  <button onClick={() => del('household', h.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={11}/></button>
                </div>
                <div className="text-slate-500">{h.plan_year || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
